<template>
  <div class="tmap">
    <svg v-if="ready && shapes.length"
         :viewBox="viewBox"
         class="tmap-svg"
         preserveAspectRatio="xMidYMid meet"
         @pointerdown.prevent="panStart"
         @wheel.prevent="onWheel"
         @mouseleave="onLeave">
      <path v-for="s in orderedShapes"
            :key="s.postal"
            :d="s.d"
            :fill="s.fill"
            class="tmap-state"
            :style="{
              stroke: selectedState.indexOf(s.postal) >= 0 ? '#111111' : s.stroke,
              strokeWidth: selectedState.indexOf(s.postal) >= 0 ? '2.6' : (s.out ? '1.1' : '0.6'),
              opacity: 1
            }"
            @click="onStateClick(s.postal)"
            @mouseenter="onHover(s)">
        <title>{{ (selectedState.indexOf(s.postal) >= 0 ? '✓ filtering by ' : 'Click to filter by ') + s.title }}</title>
      </path>
    </svg>
    <div v-else class="tmap-empty">No regional data for the current filters.</div>

    <div class="tmap-zoom" v-if="ready && shapes.length">
      <button type="button" @click="zoomOut" title="Zoom out" aria-label="Zoom out">&minus;</button>
      <button type="button" @click="zoomIn" title="Zoom in" aria-label="Zoom in">+</button>
    </div>

  </div>
</template>

<script>
import { geoAlbersUsa, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topo from "@/assets/usa.states.topo.json";

export default {
  name: "TerritoryMap",
  props: {
    // [{ state: 'TX', territory: 'SouthEast', sales, orderCount }]
    byState: { type: Array, default: () => [] },
    // (territoryName) => css color
    colorFor: { type: Function, required: true },
    // Optional explicit legend [{ name, color }]; when given it overrides the
    // legend derived from byState (lets us list territories — e.g. International —
    // that don't color any single state on the map).
    legendItems: { type: Array, default: null },
    // Currently-filtered state postal — full color while others dim.
    selectedState: { type: Array, default: () => [] },
    // Currently-filtered territories (multi) — only their states stay colored;
    // everything else goes white fill with a red border.
    selectedTerritory: { type: Array, default: () => [] },
    // Zoom level (1 = full US / zoomed out). Owned + persisted by the parent.
    zoom: { type: Number, default: 1 },
  },
  data() {
    return { width: 720, height: 420, ready: false, shapes: [], panX: 0, panY: 0 };
  },
  computed: {
    // postal (TX) -> its aggregate row
    stateMap() {
      const m = {};
      (this.byState || []).forEach((s) => {
        if (s && s.state) m[String(s.state).toUpperCase()] = s;
      });
      return m;
    },
    // distinct territories present, for the legend
    legend() {
      if (this.legendItems && this.legendItems.length) return this.legendItems;
      const seen = {};
      const out = [];
      (this.byState || []).forEach((s) => {
        if (s && s.territory && !seen[s.territory]) {
          seen[s.territory] = 1;
          out.push({ name: s.territory, color: this.colorFor(s.territory) });
        }
      });
      return out;
    },
    // Zoom by shrinking the viewBox around the center (1 = full extent).
    viewBox() {
      var z = this.zoom > 0 ? this.zoom : 1;
      var w = this.width / z, h = this.height / z;
      var x = (this.width - w) / 2 + (this.panX || 0), y = (this.height - h) / 2 + (this.panY || 0);
      return x.toFixed(1) + " " + y.toFixed(1) + " " + w.toFixed(1) + " " + h.toFixed(1);
    },
    // Selected states render LAST so their bold standout border isn't overdrawn by a neighbor's fill.
    orderedShapes() {
      var sel = this.selectedState || [];
      if (!sel.length) return this.shapes;
      return this.shapes.slice().sort(function (a, b) {
        return (sel.indexOf(a.postal) >= 0 ? 1 : 0) - (sel.indexOf(b.postal) >= 0 ? 1 : 0);
      });
    },
  },
  watch: {
    byState() { this.recolor(); },
    // deep: filters.territory is mutated in place (push/splice), so a shallow watch wouldn't fire.
    selectedTerritory: { deep: true, handler() { this.recolor(); } },
  },
  mounted() { this.build(); },
  beforeDestroy() {
    window.removeEventListener("pointermove", this.panMove);
    window.removeEventListener("pointerup", this.panEnd);
  },
  methods: {
    // Project the topojson once into SVG path strings (geometry never changes).
    build() {
      try {
        const fc = feature(topo, topo.objects.states);
        const projection = geoAlbersUsa().fitSize([this.width, this.height], fc);
        const path = geoPath(projection);
        this._features = (fc.features || []).map((ft) => ({
          postal: ((ft.properties && ft.properties.postal) || "").toUpperCase(),
          name: (ft.properties && ft.properties.name) || "",
          d: path(ft),
        }));
        this.ready = true;
        this.recolor();
      } catch (e) {
        this.ready = false;
      }
    },
    // Re-fill states by their territory whenever the data changes.
    recolor() {
      if (!this._features) return;
      const selT = this.selectedTerritory || [];
      const hasT = selT.length > 0;
      this.shapes = this._features.map((f) => {
        const rec = this.stateMap[f.postal];
        const out = hasT && !(rec && selT.indexOf(rec.territory) >= 0);   // a territory is filtered & this state isn't in it
        let fill, stroke;
        if (out) { fill = "#ffffff"; stroke = "#9e2436"; }
        else { fill = rec ? this.colorFor(rec.territory) : "#e9e9ec"; stroke = "#ffffff"; }
        return {
          postal: f.postal,
          d: f.d,
          fill: fill,
          stroke: stroke,
          out: out,
          name: f.name,
          territory: rec ? rec.territory : null,
          sales: rec ? (Number(rec.sales) || 0) : 0,
          hasData: !!rec,
          title: rec
            ? f.name + " — " + rec.territory + " · $" + (Number(rec.sales) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })
            : f.name + " — no sales",
        };
      });
    },
    // Zoom controls — emit the clamped new level; the parent stores + persists it.
    zoomIn() { this.emitZoom(this.zoom * 1.25); },
    zoomOut() { this.emitZoom(this.zoom / 1.25); },
    emitZoom(z) {
      var c = Math.min(5, Math.max(0.5, Math.round(z * 100) / 100));
      this.$emit("zoom-change", c);
    },
    // Mouse-wheel zoom (in = scroll up). preventDefault on the <svg> stops the page scrolling under it.
    onWheel(e) {
      var f = (e.deltaY < 0) ? 1.15 : 1 / 1.15;
      this.emitZoom((this.zoom > 0 ? this.zoom : 1) * f);
    },
    // Hovering a state surfaces its flag + stats in the parent's panel below the map.
    onHover(s) { this.$emit("hover-state", { postal: s.postal, name: s.name, territory: s.territory, sales: s.sales, hasData: s.hasData }); },
    onLeave() { this.$emit("hover-state", null); },
    // Click a state to filter — unless the pointer was just dragging the map (pan), which would
    // otherwise fire a stray filter on drag-release.
    onStateClick(postal) { if (this._justDragged) return; this.$emit("select-state", postal); },
    // Drag-to-pan: offset the viewBox by the pointer delta (converted from screen px to viewBox units).
    panStart(e) {
      this._pan = { sx: e.clientX, sy: e.clientY, px: this.panX, py: this.panY, w: (e.currentTarget && e.currentTarget.clientWidth) || this.width };
      this._moved = false;
      window.addEventListener("pointermove", this.panMove);
      window.addEventListener("pointerup", this.panEnd);
    },
    panMove(e) {
      if (!this._pan) return;
      var dx = e.clientX - this._pan.sx, dy = e.clientY - this._pan.sy;
      if (Math.abs(dx) + Math.abs(dy) > 3) this._moved = true;
      var z = this.zoom > 0 ? this.zoom : 1;
      var scale = (this.width / z) / (this._pan.w || this.width);
      this.panX = this._pan.px - dx * scale;
      this.panY = this._pan.py - dy * scale;
    },
    panEnd() {
      this._pan = null;
      window.removeEventListener("pointermove", this.panMove);
      window.removeEventListener("pointerup", this.panEnd);
      if (this._moved) { var self = this; this._justDragged = true; setTimeout(function () { self._justDragged = false; }, 60); }
    },
  },
};
</script>

<style scoped>
.tmap { width: 100%; position: relative; }
.tmap-svg { width: 100%; height: auto; display: block; cursor: grab; touch-action: none; }
.tmap-svg:active { cursor: grabbing; }
.tmap-zoom { position: absolute; top: 6px; right: 6px; display: flex; flex-direction: column; gap: 4px; z-index: 2; }
.tmap-zoom button { width: 24px; height: 24px; border: 1px solid #d4d4d8; background: rgba(255,255,255,.92); color: #555; font-size: 16px; font-weight: 700; line-height: 1; border-radius: 4px; cursor: pointer; padding: 0; }
.tmap-zoom button:hover { background: #9e2436; color: #fff; border-color: #9e2436; }
.tmap-state { stroke: #ffffff; stroke-width: 0.6; transition: opacity 0.15s; cursor: pointer; }
.tmap-state:hover { opacity: 0.78; }
.tmap-state--sel { stroke: #1a1a1a; stroke-width: 1.6; }
.tmap-empty { padding: 30px; text-align: center; color: #999; font-size: 13px; }
.tmap-legend {
  display: flex; flex-wrap: wrap; gap: 6px 16px;
  padding: 10px 14px 2px; font-size: 11.5px; color: #555;
}
.tmap-legend-item { display: inline-flex; align-items: center; }
.tmap-legend-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 6px; }
</style>
